import { FileMagnifyingGlassIcon } from '@phosphor-icons/react'
import { useTheme } from '@pixee/design-system'
import classNames from 'classnames'
import { match, P } from 'ts-pattern'
import AikidoIconDk from '../../assets/SastToolVisualAssets/Aikido-Icon-Dk.png?url'
import AikidoIconLt from '../../assets/SastToolVisualAssets/Aikido-Icon-Lt.png?url'
import AppScanIcon from '../../assets/SastToolVisualAssets/AppScan-Icon.png?url'
import ArnicaIcon from '../../assets/SastToolVisualAssets/Arnica-Icon.png?url'
import CheckmarxIcon from '../../assets/SastToolVisualAssets/Checkmarx-Icon.svg?react'
import CodeQLIcon from '../../assets/SastToolVisualAssets/CodeQL-Logo.svg?react'
import ContrastIcon from '../../assets/SastToolVisualAssets/Contrast-Icon-Dk.svg?react'
import ContrastIconLt from '../../assets/SastToolVisualAssets/Contrast-Icon-Lt.svg?react'
import DatadogIcon from '../../assets/SastToolVisualAssets/Datadog-Icon-Dk.svg?react'
import DatadogIconLt from '../../assets/SastToolVisualAssets/Datadog-Icon-Lt.svg?react'
import DefectDojoIcon from '../../assets/SastToolVisualAssets/DefectDojo-Icon.svg?react'
import FortifyIcon from '../../assets/SastToolVisualAssets/Fortify-Icon.svg?react'
import DependabotIcon from '../../assets/SastToolVisualAssets/GitHub-Dependabot-Icon.svg?react'
import GitLabSASTIcon from '../../assets/SastToolVisualAssets/GitLab-SAST-Icon.svg?react'
import GitleaksIcon from '../../assets/SastToolVisualAssets/Gitleaks-Icon.png?url'
import PixeeIconDk from '../../assets/SastToolVisualAssets/Pixee-Icon-Dk.png?url'
import PixeeIconLt from '../../assets/SastToolVisualAssets/Pixee-Icon-Lt.png?url'
import BlackDuckPolarisIcon from '../../assets/SastToolVisualAssets/Polaris-Icon.svg?react'
import SemgrepIcon from '../../assets/SastToolVisualAssets/Semgrep-Icon.svg?react'
import SnykIcon from '../../assets/SastToolVisualAssets/Snyk-Icon-Dk.svg?react'
import SnykIconLt from '../../assets/SastToolVisualAssets/Snyk-Icon-Lt.svg?react'
import SonarIcon from '../../assets/SastToolVisualAssets/Sonar-Icon-Dk.svg?react'
import SonarIconLt from '../../assets/SastToolVisualAssets/Sonar-Icon-Lt.svg?react'
import VeracodeIcon from '../../assets/SastToolVisualAssets/Veracode-Icon-Dk.svg?react'
import VeracodeIconLt from '../../assets/SastToolVisualAssets/Veracode-Icon-Lt.svg?react'
import ZapIcon from '../../assets/SastToolVisualAssets/ZAP-Icon.svg?react'
import type { Tool } from '../../utils/api-client/api-schemas'
import { getToolName } from '../../utils/repository-utils'
import * as styles from './tool-icon.css.ts'
import type { ToolBrandingSize } from './tool-utils'

type ToolIconProps = {
  tool?: Tool
  size?: ToolBrandingSize
  className?: string
  withAriaLabel?: boolean
}

/**
 * Returns the appropriate tool icon component based on the tool type
 * @param tool - The tool type
 * @param size - The size of the icon (small 16px, medium 20px, medium+ 24px, large 32px, large+ 64px)
 * @param className - Optional CSS class name to apply to the icon
 * @param withAriaLabel - Whether to add an aria-label to the icon. Turn on only if relevant to screen reader users (and tests).
 * @returns A React component representing the tool's icon
 */
export const ToolIcon = ({ tool, size = 'medium', className, withAriaLabel = false }: ToolIconProps) => {
  const { theme } = useTheme()
  const sizeClassName = classNames(styles.icon({ size }), className)

  return match(tool)
    .with(undefined, () => <div className={sizeClassName} />)
    .with('aikido_sast', 'aikido_sca', tool => (
      <img
        src={theme === 'dark' ? AikidoIconDk : AikidoIconLt}
        alt={withAriaLabel ? `${getToolName(tool)} icon` : undefined}
        aria-label={withAriaLabel ? `${getToolName(tool)} icon` : undefined}
        className={sizeClassName}
      />
    ))
    .with('appscan', () => (
      <img
        src={AppScanIcon}
        alt={withAriaLabel ? 'AppScan icon' : undefined}
        aria-label={withAriaLabel ? 'AppScan icon' : undefined}
        className={sizeClassName}
      />
    ))
    .with('polaris_sast', 'polaris_sca', () => (
      <BlackDuckPolarisIcon
        aria-label={withAriaLabel ? 'Black Duck Polaris icon' : undefined}
        className={sizeClassName}
      />
    ))
    .with('black_duck_hub_sca', tool => (
      <BlackDuckPolarisIcon
        aria-label={withAriaLabel ? `${getToolName(tool)} icon` : undefined}
        className={classNames(sizeClassName, styles.monochromeMark)}
      />
    ))
    .with('checkmarx', () => (
      <CheckmarxIcon aria-label={withAriaLabel ? 'Checkmarx icon' : undefined} className={sizeClassName} />
    ))
    .with('codeql', () => (
      <CodeQLIcon aria-label={withAriaLabel ? 'CodeQL icon' : undefined} className={sizeClassName} />
    ))
    .with('contrast', () =>
      theme === 'dark' ? (
        <ContrastIcon aria-label={withAriaLabel ? 'Contrast icon' : undefined} className={sizeClassName} />
      ) : (
        <ContrastIconLt aria-label={withAriaLabel ? 'Contrast icon' : undefined} className={sizeClassName} />
      )
    )
    .with('defectdojo', () => (
      <DefectDojoIcon aria-label={withAriaLabel ? 'DefectDojo icon' : undefined} className={sizeClassName} />
    ))
    .with('fortify', () => (
      <FortifyIcon aria-label={withAriaLabel ? 'Fortify icon' : undefined} className={sizeClassName} />
    ))
    .with(P.union('gitlab_sast', 'gitlab_dependency_scanning'), tool => (
      <GitLabSASTIcon aria-label={withAriaLabel ? `${getToolName(tool)} icon` : undefined} className={sizeClassName} />
    ))
    .with('dependabot', () => (
      <DependabotIcon aria-label={withAriaLabel ? 'Dependabot icon' : undefined} className={sizeClassName} />
    ))
    .with('semgrep', () => (
      <SemgrepIcon aria-label={withAriaLabel ? 'Semgrep icon' : undefined} className={sizeClassName} />
    ))
    .with('snyk', () =>
      theme === 'dark' ? (
        <SnykIcon aria-label={withAriaLabel ? 'Snyk icon' : undefined} className={sizeClassName} />
      ) : (
        <SnykIconLt aria-label={withAriaLabel ? 'Snyk icon' : undefined} className={sizeClassName} />
      )
    )
    .with('sonar', () =>
      theme === 'dark' ? (
        <SonarIcon aria-label={withAriaLabel ? 'SonarQube icon' : undefined} className={sizeClassName} />
      ) : (
        <SonarIconLt aria-label={withAriaLabel ? 'SonarQube icon' : undefined} className={sizeClassName} />
      )
    )
    .with('veracode', () =>
      theme === 'dark' ? (
        <VeracodeIcon aria-label={withAriaLabel ? 'Veracode icon' : undefined} className={sizeClassName} />
      ) : (
        <VeracodeIconLt aria-label={withAriaLabel ? 'Veracode icon' : undefined} className={sizeClassName} />
      )
    )
    .with('datadog_sast', () =>
      theme === 'dark' ? (
        <DatadogIcon aria-label={withAriaLabel ? `${getToolName(tool)} icon` : undefined} className={sizeClassName} />
      ) : (
        <DatadogIconLt aria-label={withAriaLabel ? `${getToolName(tool)} icon` : undefined} className={sizeClassName} />
      )
    )
    .with('arnica_sast', () => (
      <img
        src={ArnicaIcon}
        alt={withAriaLabel ? 'Arnica icon' : undefined}
        aria-label={withAriaLabel ? 'Arnica icon' : undefined}
        className={sizeClassName}
      />
    ))
    .with('gitleaks', () => (
      <img
        src={GitleaksIcon}
        alt={withAriaLabel ? 'Gitleaks icon' : undefined}
        aria-label={withAriaLabel ? 'Gitleaks icon' : undefined}
        className={sizeClassName}
        style={theme === 'dark' ? { backgroundColor: 'white' } : { backgroundColor: 'black', filter: 'invert(1)' }}
      />
    ))
    .with('zap', () => <ZapIcon aria-label={withAriaLabel ? 'OWASP ZAP icon' : undefined} className={sizeClassName} />)
    .with('pixee_ai', () => (
      <img
        src={theme === 'dark' ? PixeeIconDk : PixeeIconLt}
        alt={withAriaLabel ? 'Pixee AI Scanner icon' : undefined}
        aria-label={withAriaLabel ? 'Pixee AI Scanner icon' : undefined}
        className={sizeClassName}
      />
    ))
    .with('freeform', () => (
      <FileMagnifyingGlassIcon
        weight="light"
        aria-label={withAriaLabel ? 'Findings intake icon' : undefined}
        className={sizeClassName}
      />
    ))
    .exhaustive()
}
